import apiClient from '@/lib/api-client';

// Types for the availability-based booking system

export interface TimeSlot {
  start_time: string;
  end_time: string;
  formatted_start: string;
  formatted_end: string;
  provider_id: number;
  provider_name?: string;
  provider_public_id?: string;
  service_id: number;
  duration_minutes: number;
}

export type RecurrenceType = 'weekly' | 'biweekly' | 'monthly';
export type EndType = 'never' | 'on_date' | 'after_occurrences';

export interface ProviderAvailability {
  id: number;
  kind?: 'one_to_one' | 'group_class';
  day_of_week: number;
  day_name: string;
  start_time: string;
  end_time: string;
  formatted_range: string;
  duration_minutes: number;
  active: boolean;
  // Recurrence fields
  recurrence_type: RecurrenceType;
  recurrence_interval: number;
  monthly_week: number | null;
  monthly_day: number | null;
  end_type: EndType;
  end_date: string | null;
  effective_from: string | null;
  occurrences: number | null;
  recurrence_group_id: string | null;
  recurrence_summary: string;
  // Services
  services: { id: number; name: string }[];
  service_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface WeekScheduleDay {
  day_of_week: number;
  day_name: string;
  time_ranges: ProviderAvailability[];
}

export interface AvailabilityOverride {
  id: number;
  date: string;
  formatted_date: string;
  override_type: 'unavailable' | 'modified' | 'extra';
  start_time: string | null;
  end_time: string | null;
  formatted_range: string;
  reason: string | null;
  description: string;
  active: boolean;
  recurrence_group_id: string | null;
  services: { id: number; name: string }[];
  service_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface BookingSettings {
  id: number;
  buffer_minutes: number;
  slot_interval_minutes: number;
  advance_booking_days: number;
  minimum_notice_hours: number;
  buffer_description: string;
  interval_description: string;
  advance_description: string;
  notice_description: string;
  earliest_bookable_time: string;
  latest_bookable_date: string;
  created_at: string;
  updated_at: string;
}

export interface AvailableProvider {
  id: number;
  public_id: string;
  name: string;
  title: string;
  bio?: string;
  color?: string;
  slot_count: number;
  first_available: string;
}

export interface DatesAvailability {
  dates: Record<string, 'available' | 'fully_booked' | 'unavailable'>;
  available_dates: string[];
  fully_booked_dates: string[];
  unavailable_dates: string[];
}

// Group-class slot detail returned by show/update endpoints — includes the
// participants list so the edit dialog can show who's coming.
export interface StaffGroupClassParticipant {
  id: number;
  public_id: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  customer_name: string;
  customer_email: string;
  created_at: string;
}

export interface StaffGroupClassSlotDetail {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  service: { id: number; name: string; duration_minutes: number };
  provider_id: number;
  provider?: { id: number; display_name: string | null };
  max_capacity: number;
  current_bookings: number;
  waitlist_count?: number;
  waitlist_enabled?: boolean;
  cancelled?: boolean;
  cancelled_at?: string | null;
  // Set when the class was materialised from a recurring rule. `series_remaining`
  // counts this occurrence plus every later one still on the schedule — what a
  // "cancel this and all following" would take out.
  recurring?: boolean;
  series_remaining?: number;
  participants: StaffGroupClassParticipant[];
}

export type GroupClassCancelScope = 'single' | 'series';

export interface GroupClassCancelResult {
  id: number;
  cancelled: boolean;
  // True when nothing is left on the calendar for the cancelled occurrences —
  // no follow-up "remove from schedule" is needed.
  removed: boolean;
  cancelled_count: number;
  series_ended: boolean;
  participants_notified: number;
  waitlist_notified?: number;
}

// Public availability API (for customers)
export const availabilityService = {
  // Get available time slots for a specific date
  async getSlots(params: {
    organization_id: number | string;
    service_id: number;
    date: string;
    provider_id?: number;
  }): Promise<TimeSlot[]> {
    const { data } = await apiClient.get<{ data: TimeSlot[] }>('/v1/availability/slots', {
      params
    });
    return data.data;
  },

  // Get dates availability for calendar highlighting
  async getDatesAvailability(params: {
    organization_id: number | string;
    service_id: number;
    start_date: string;
    end_date: string;
    provider_id?: number;
  }): Promise<DatesAvailability> {
    const { data } = await apiClient.get<{ data: DatesAvailability }>('/v1/availability/dates', {
      params
    });
    return data.data;
  },

  // Get providers available on a specific date
  async getAvailableProviders(params: {
    organization_id: number | string;
    service_id: number;
    date: string;
  }): Promise<AvailableProvider[]> {
    const { data } = await apiClient.get<{ data: AvailableProvider[] }>('/v1/availability/providers', {
      params
    });
    return data.data;
  }
};

// Staff availability API (for managing own schedule)
export const staffAvailabilityService = {
  // Get all recurring availabilities
  async getAvailabilities(): Promise<ProviderAvailability[]> {
    const { data } = await apiClient.get<{ data: ProviderAvailability[] }>('/v1/staff/availability');
    return data.data;
  },

  // Get weekly schedule grouped by day (pass date to get correct week)
  async getWeekSchedule(date?: string): Promise<WeekScheduleDay[]> {
    const { data } = await apiClient.get<{ data: WeekScheduleDay[] }>('/v1/staff/availability/week', {
      params: date ? { date } : undefined,
    });
    return data.data;
  },

  // Get services assigned to the current provider
  async getMyServices(): Promise<{ id: number; name: string; duration_minutes: number; active: boolean; max_capacity?: number | null }[]> {
    const { data } = await apiClient.get<{ data: { id: number; name: string; duration_minutes: number; active: boolean; max_capacity?: number | null }[] }>('/v1/staff/availability/services');
    return data.data;
  },

  // Get single availability
  async getAvailability(id: number): Promise<ProviderAvailability> {
    const { data } = await apiClient.get<{ data: ProviderAvailability }>(`/v1/staff/availability/${id}`);
    return data.data;
  },

  // Create new recurring availability
  async createAvailability(availability: {
    day_of_week: number;
    start_time: string;
    end_time: string;
  }): Promise<ProviderAvailability> {
    const { data } = await apiClient.post<{ data: ProviderAvailability }>(
      '/v1/staff/availability',
      availability
    );
    return data.data;
  },

  // Update availability
  async updateAvailability(id: number, updates: Partial<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    active: boolean;
    service_ids: number[];
  }>): Promise<ProviderAvailability> {
    const { data } = await apiClient.put<{ data: ProviderAvailability }>(
      `/v1/staff/availability/${id}`,
      updates
    );
    return data.data;
  },

  // Update availability for future occurrences only (splits if has past)
  async updateFutureAvailability(
    id: number,
    data: {
      day_of_week: number;
      start_time: string;
      end_time: string;
      recurrence_type: RecurrenceType;
      recurrence_interval: number;
      monthly_week: number | null;
      monthly_day: number | null;
      end_type: EndType;
      end_date: string | null;
      occurrences: number | null;
      service_ids?: number[];
    }
  ): Promise<ProviderAvailability> {
    const { data: response } = await apiClient.put<{ data: ProviderAvailability; meta: { split: boolean } }>(
      `/v1/staff/availability/${id}/update_future`,
      data
    );
    return response.data;
  },

  // Delete availability
  async deleteAvailability(id: number): Promise<void> {
    await apiClient.delete(`/v1/staff/availability/${id}`);
  },

  // Bulk update entire weekly schedule
  async bulkUpdateSchedule(availabilities: {
    day_of_week: number;
    start_time: string;
    end_time: string;
  }[]): Promise<ProviderAvailability[]> {
    const { data } = await apiClient.post<{ data: ProviderAvailability[] }>(
      '/v1/staff/availability/bulk',
      { availabilities }
    );
    return data.data;
  },

  // === Overrides ===

  // Get all date overrides
  async getOverrides(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<AvailabilityOverride[]> {
    const { data } = await apiClient.get<{ data: AvailabilityOverride[] }>(
      '/v1/staff/availability/overrides',
      { params }
    );
    return data.data;
  },

  // Get single override
  async getOverride(id: number): Promise<AvailabilityOverride> {
    const { data } = await apiClient.get<{ data: AvailabilityOverride }>(
      `/v1/staff/availability/overrides/${id}`
    );
    return data.data;
  },

  // Create date override
  async createOverride(override: {
    date: string;
    override_type: 'unavailable' | 'modified' | 'extra';
    start_time?: string;
    end_time?: string;
    reason?: string;
    service_ids?: number[];
  }): Promise<AvailabilityOverride> {
    const { data } = await apiClient.post<{ data: AvailabilityOverride }>(
      '/v1/staff/availability/overrides',
      override
    );
    return data.data;
  },

  // Update override
  async updateOverride(id: number, updates: Partial<{
    date: string;
    override_type: 'unavailable' | 'modified' | 'extra';
    start_time: string;
    end_time: string;
    reason: string;
    active: boolean;
    service_ids: number[];
  }>): Promise<AvailabilityOverride> {
    const { data } = await apiClient.put<{ data: AvailabilityOverride }>(
      `/v1/staff/availability/overrides/${id}`,
      updates
    );
    return data.data;
  },

  // Delete override
  async deleteOverride(id: number): Promise<void> {
    await apiClient.delete(`/v1/staff/availability/overrides/${id}`);
  },

  // Quick action: mark date as unavailable
  async markDateUnavailable(date: string, reason?: string, recurrenceGroupId?: string, startTime?: string, endTime?: string): Promise<AvailabilityOverride> {
    const { data } = await apiClient.post<{ data: AvailabilityOverride }>(
      '/v1/staff/availability/mark_unavailable',
      { date, reason, recurrence_group_id: recurrenceGroupId, start_time: startTime, end_time: endTime }
    );
    return data.data;
  },

  // === Multi-day & Date Range Creation ===

  // Create multiple availability records at once (with recurrence support)
  async createMultiDayAvailability(params: {
    days_of_week: number[];
    start_time: string;
    end_time: string;
    recurrence_type: RecurrenceType;
    recurrence_interval?: number;
    monthly_week?: number | null;
    monthly_day?: number | null;
    end_type: EndType;
    end_date?: string | null;
    occurrences?: number | null;
    effective_from?: string;
    service_ids?: number[];
    force_override?: boolean;
  }): Promise<ProviderAvailability[]> {
    const { data } = await apiClient.post<{ data: ProviderAvailability[] }>(
      '/v1/staff/availability/multi_day',
      params
    );
    return data.data;
  },

  // Create date range availability (extra hours for specific date range)
  async createDateRangeAvailability(params: {
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    reason?: string;
    service_ids?: number[];
  }): Promise<AvailabilityOverride[]> {
    const { data } = await apiClient.post<{ data: AvailabilityOverride[] }>(
      '/v1/staff/availability/date_range',
      params
    );
    return data.data;
  },

  // Create a recurring group-class rule (kind=group_class). Server materialises
  // one AvailableSlot per occurrence. Returns the slot count for the success toast.
  async createGroupClassRecurringAvailability(params: {
    service_id: number;
    days_of_week: number[];
    start_time: string;
    recurrence_type: RecurrenceType;
    recurrence_interval?: number;
    monthly_week?: number | null;
    monthly_day?: number | null;
    end_type: EndType;
    end_date?: string | null;
    occurrences?: number | null;
    effective_from?: string;
  }): Promise<{ blocks: ProviderAvailability[]; group_id: string; slots_created: number }> {
    const { service_id, ...rest } = params;
    const { data } = await apiClient.post<{
      data: ProviderAvailability[];
      meta?: { group_id?: string; slots_created?: number };
    }>('/v1/staff/availability/multi_day', {
      ...rest,
      kind: 'group_class',
      service_ids: [service_id],
    });
    return {
      blocks: data.data,
      group_id: data.meta?.group_id ?? '',
      slots_created: data.meta?.slots_created ?? 0,
    };
  },

  // Create a one-off group-class AvailableSlot for the current provider.
  async createGroupClassOneTimeAvailability(params: {
    service_id: number;
    date: string;
    start_time: string;
  }): Promise<{ id: number; start_time: string; end_time: string; max_capacity: number }> {
    const { data } = await apiClient.post<{
      data: { id: number; start_time: string; end_time: string; max_capacity: number };
    }>('/v1/staff/availability/group_class_one_time', params);
    return data.data;
  },

  // Cancel a group-class instance owned by the current provider — or, with
  // scope 'series', that instance and every later one from the same recurring
  // rule. A class nobody had booked leaves the schedule straight away
  // (`removed: true`); one with participants is marked cancelled and swept up
  // shortly after, once the emails have gone out.
  async cancelGroupClassSlot(
    slotId: number,
    scope: GroupClassCancelScope = 'single',
  ): Promise<GroupClassCancelResult> {
    const { data } = await apiClient.delete<{ data: GroupClassCancelResult }>(
      `/v1/staff/availability/group_class_slots/${slotId}`,
      { params: { scope } },
    );
    return data.data;
  },

  // Hard-delete a previously-cancelled group class. Refused if not cancelled;
  // succeeds (idempotently) if it is already gone.
  async removeGroupClassSlot(slotId: number): Promise<{ id: number; removed: boolean }> {
    const { data } = await apiClient.delete<{ data: { id: number; removed: boolean } }>(
      `/v1/staff/availability/group_class_slots/${slotId}/remove`
    );
    return data.data;
  },

  // Slot detail + participants list for the edit dialog.
  async getGroupClassSlot(slotId: number): Promise<StaffGroupClassSlotDetail> {
    const { data } = await apiClient.get<{ data: StaffGroupClassSlotDetail }>(
      `/v1/staff/availability/group_class_slots/${slotId}`
    );
    return data.data;
  },

  // Move the class to a new date/time. End_time is recomputed server-side.
  async updateGroupClassSlot(
    slotId: number,
    params: { date?: string; start_time?: string }
  ): Promise<StaffGroupClassSlotDetail> {
    const { data } = await apiClient.patch<{ data: StaffGroupClassSlotDetail }>(
      `/v1/staff/availability/group_class_slots/${slotId}`,
      params
    );
    return data.data;
  },

  // End a recurrence group from a specific date forward
  async endGroup(groupId: string, date: string): Promise<any> {
    const { data } = await apiClient.put(
      `/v1/staff/availability/group/${groupId}/end`,
      { date }
    );
    return data.data;
  },

  // Delete all availabilities in a recurrence group
  async deleteGroup(groupId: string): Promise<{ deleted: boolean; count: number }> {
    const { data } = await apiClient.delete<{ data: { deleted: boolean; count: number } }>(
      `/v1/staff/availability/group/${groupId}`
    );
    return data.data;
  },

  // Update all availabilities in a recurrence group (Google Calendar-style series edit)
  // If days_of_week is provided, will add/remove days from the series
  async updateGroup(groupId: string, updates: {
    start_time: string;
    end_time: string;
    recurrence_type?: RecurrenceType;
    recurrence_interval?: number;
    end_type?: EndType;
    end_date?: string | null;
    occurrences?: number | null;
    service_ids?: number[];
    days_of_week?: number[];
  }): Promise<{ message: string; count: number; availabilities: ProviderAvailability[] }> {
    const { data } = await apiClient.put<{
      data: { message: string; count: number; availabilities: ProviderAvailability[] }
    }>(
      `/v1/staff/availability/group/${groupId}`,
      updates
    );
    return data.data;
  }
};

// Staff booking settings API
export const staffBookingSettingsService = {
  // Get current settings
  async getSettings(): Promise<BookingSettings> {
    const { data } = await apiClient.get<{
      data: BookingSettings;
      meta: {
        buffer_options: number[];
        interval_options: number[];
      };
    }>('/v1/staff/booking_settings');
    return data.data;
  },

  // Update settings
  async updateSettings(settings: Partial<{
    buffer_minutes: number;
    slot_interval_minutes: number;
    advance_booking_days: number;
    minimum_notice_hours: number;
  }>): Promise<BookingSettings> {
    const { data } = await apiClient.put<{ data: BookingSettings }>(
      '/v1/staff/booking_settings',
      settings
    );
    return data.data;
  },

  // Reset to defaults
  async resetSettings(): Promise<BookingSettings> {
    const { data } = await apiClient.put<{ data: BookingSettings }>(
      '/v1/staff/booking_settings/reset'
    );
    return data.data;
  }
};

export default {
  availability: availabilityService,
  staffAvailability: staffAvailabilityService,
  staffBookingSettings: staffBookingSettingsService
};
